import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Video } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import MeetingRoom from '@/components/meetings/MeetingRoom';
import meetingsService, { type RtcAccess } from '@/services/meetings.service';

/**
 * Öffentliche Gastseite — /meet/:code, OHNE Login.
 *
 * Zutritt trotzdem dreifach gesichert: unerratbarer Link-Code + eingeladene
 * E-Mail-Adresse + PIN aus der Einladungs-E-Mail. Erst nach erfolgreicher
 * Prüfung gibt der Server ein befristetes Agora-Token heraus.
 */
export default function MeetGuest() {
  const { code = '' } = useParams();
  const { t } = useTranslation();

  // Davet mailindeki linkten gelen adres — otomatik dolu, yalnız PIN kalır.
  const [searchParams] = useSearchParams();
  const prefilledEmail = (searchParams.get('email') || '').trim().toLowerCase();
  const [email, setEmail] = useState(prefilledEmail);
  const [pin, setPin] = useState('');
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewOnly, setViewOnly] = useState(false);
  const [session, setSession] = useState<{ rtc: RtcAccess; displayName: string; title: string | null; email: string; pin: string } | null>(null);

  const { data: info, isLoading } = useQuery({
    queryKey: ['meet-guest', code],
    queryFn: () => meetingsService.guestInfo(code),
    retry: false,
  });

  const join = async () => {
    setJoining(true);
    setError(null);
    try {
      const r = await meetingsService.guestJoin(code, email.trim(), pin.trim());
      setSession({ rtc: r.rtc, displayName: r.displayName, title: r.meeting.title, email: email.trim(), pin: pin.trim() });
    } catch (e: any) {
      setError(e?.response?.data?.message || t('shell:meet.joinFailed', 'Beitritt fehlgeschlagen'));
    } finally {
      setJoining(false);
    }
  };

  if (session) {
    return (
      <div className="h-screen">
        <MeetingRoom
          rtc={session.rtc}
          displayName={session.displayName}
          title={session.title}
          viewOnly={viewOnly}
          onHeartbeat={(count) => void meetingsService.guestHeartbeat(code, count).catch(() => {})}
          peers={{ get: () => meetingsService.guestPeers(code, session.email, session.pin) }}
          onLeave={() => setSession(null)}
        />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="mb-5 flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
            <Video className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <h1 className="text-base font-semibold text-slate-900">
              {isLoading
                ? '…'
                : info?.title || t('shell:meet.defaultTitle', 'Video-Gespräch')}
            </h1>
            <p className="text-xs text-slate-500">{t('shell:meet.guestSubtitle', 'Beitritt mit Ihrer Einladung')}</p>
          </div>
        </div>

        {!isLoading && info && !info.active && (
          <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            {t('shell:meet.endedInfo', 'Dieses Gespräch ist bereits beendet.')}
          </p>
        )}

        {!isLoading && !info && (
          <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {t('shell:meet.notFound', 'Dieses Gespräch existiert nicht.')}
          </p>
        )}

        {info?.active && (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="guest-email">{t('shell:meet.yourEmail', 'Ihre E-Mail-Adresse')}</Label>
              <Input
                id="guest-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@example.com"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="guest-pin">{t('shell:meet.yourPin', 'Zugangscode aus der E-Mail')}</Label>
              <Input
                id="guest-pin"
                autoFocus={Boolean(prefilledEmail)}
                inputMode="numeric"
                maxLength={6}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                onKeyDown={(e) => { if (e.key === 'Enter') void join(); }}
                placeholder="123456"
                className="text-center text-lg tracking-[6px]"
              />
            </div>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-600">
              <input
                type="checkbox"
                checked={viewOnly}
                onChange={(e) => setViewOnly(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300"
              />
              {t('shell:meet.viewOnly', 'Nur zusehen (ohne Mikrofon & Kamera)')}
            </label>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <Button
              className="h-10 w-full"
              onClick={() => void join()}
              disabled={joining || !email.trim() || pin.length !== 6}
            >
              {joining && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('shell:meet.joinNow', 'Beitreten')}
            </Button>
            <p className="text-center text-[11px] leading-4 text-slate-400">
              {t('shell:meet.privacyHint', 'Mit dem Beitritt aktivieren Sie Mikrofon und ggf. Kamera Ihres Geräts.')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
